import { createContext, useEffect, useState, ReactNode } from "react";
import Keycloak from "keycloak-js";
import {
  VITE_KEYCLOAK_CLIENT,
  VITE_KEYCLOAK_REALM,
  VITE_KEYCLOAK_URL,
} from "../../config";
import { persistor, store } from "../../redux/store/store";
import { setIsDeviceRegistered } from "../../redux/slices/auth.slice";

const hostName = window.location.hostname;
const pathName = window.location.pathname;

export const getRealmName = () => {
  if (hostName === "localhost") {
    return localStorage.getItem("realm") || pathName.split("/")[1];
  }
  return hostName.split("-")[0];
};

const keycloak = new Keycloak({
  url: VITE_KEYCLOAK_URL,
  realm: VITE_KEYCLOAK_REALM,
  clientId: VITE_KEYCLOAK_CLIENT,
});

const AuthContext = createContext<Keycloak>(keycloak);

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const initKeycloak = async () => {
      try {
        const authenticated = await keycloak.init({
          onLoad: "check-sso",
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          redirectUri: window.location.href,
          enableLogging: true,
          checkLoginIframe: false,
          pkceMethod: "S256",
        });

        if (authenticated) {
          localStorage.setItem("access_token", keycloak.token as string);
        }

        const urlParams = new URLSearchParams(window.location.search);
        const hasShareToken = urlParams.has("shareToken");

        const isPublicRoute =
          window.location.pathname.startsWith("/auth/") ||
          window.location.pathname.startsWith("/maps") ||
          (window.location.pathname === "/admin/maps" && hasShareToken) ||
          window.location.pathname === "/";

        if (!authenticated && !isPublicRoute) {
          const currentPath = window.location.pathname;
          keycloak.login({
            redirectUri:
              currentPath === "/"
                ? `${window.location.origin}/dashboard`
                : window.location.href,
          });
        }

        setInitialized(true);
      } catch (error) {
        console.error("Keycloak init failed:", error);
        setInitialized(true);
      }
    };

    initKeycloak();

    const refreshInterval = setInterval(() => {
      if (!keycloak.authenticated || !keycloak.refreshToken) return;
      keycloak
        .updateToken(120)
        .then((refreshed) => {
          if (refreshed) {
            localStorage.setItem("access_token", keycloak.token as string);
          }
        })
        .catch((err) => {
          console.warn("Background token refresh failed:", err);
        });
    }, 60_000);

    return () => clearInterval(refreshInterval);
  }, []);

  if (!initialized) return null;

  return (
    <AuthContext.Provider value={keycloak}>{children}</AuthContext.Provider>
  );
};

export const getAccessToken = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!keycloak.authenticated) {
      const token = localStorage.getItem("access_token");
      if (token) return resolve(token);
      keycloak.login({
        redirectUri: window.location.href,
      });
      return reject("Not logged in");
    }

    keycloak
      .updateToken(60)
      .then(() => {
        const token = keycloak.token as string;
        localStorage.setItem("access_token", token);
        resolve(token);
      })
      .catch(() => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("realm");
        keycloak.login({
          redirectUri: window.location.href,
        });
        reject("Failed to refresh token");
      });
  });
};

export const logoutUser = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("realm");
  store.dispatch(setIsDeviceRegistered(false));
  persistor.purge();

  keycloak.logout({
    redirectUri:
      hostName === "localhost"
        ? `${window.location.origin}/dashboard`
        : window.location.origin,
  });
};

export default AuthContext;
